// Per-level sky gradient palettes.
//
// Levels 1-10 use loaded background IMAGES (see LEVEL_BACKGROUNDS in
// constants.js). Levels 11-20 don't have dedicated sky images — they use
// the gradient palette here, aligned with the ground theme name in
// groundThemes.js so each level has a coherent visual identity:
//
//   11 ice        → arctic blue
//   12 snow       → pale twilight
//   13 jungle     → dense green
//   14 swamp      → olive murk
//   15 metal grate → cold steel
//   16 space station → cosmic purple
//   17 castle     → dusk stone
//   18 ruined city → apocalyptic sunset
//   19 crystal cavern → violet crystal
//   20 final boss → hell red
//
// Each entry provides:
//   sky:      3-stop vertical gradient (top → horizon)
//   building: hex color for silhouette layer
//   accent:   hex color for lit windows / detail

export const LEVEL_GRADIENTS = {
    1:  { sky: ['#2a1a2e', '#5a2818', '#8a3a1a'], building: '#3a251a', accent: '#FF9500' }, // Rustic Dusk City
    2:  { sky: ['#0a0a0a', '#1a1a2e', '#16213e'], building: '#1f1f3a', accent: '#9945FF' }, // Urban Alley
    3:  { sky: ['#3a1a08', '#5c2a12', '#7c3a1e'], building: '#3a251a', accent: '#FFB347' }, // Desert Sand
    4:  { sky: ['#3a1a08', '#5c2a12', '#7c3a1e'], building: '#3a251a', accent: '#FFB347' }, // Desert Highway
    5:  { sky: ['#1a0a0a', '#2e1a1a', '#3e1616'], building: '#3a1f1f', accent: '#FF6B6B' }, // Industrial Cement
    6:  { sky: ['#1a0a0a', '#2e1a1a', '#3e1616'], building: '#3a1f1f', accent: '#FF6B6B' }, // Factory Floor
    7:  { sky: ['#0a1a0a', '#1a2e1a', '#163e16'], building: '#1f3a1f', accent: '#4ADE80' }, // Wooden Bridge
    8:  { sky: ['#0a1a0a', '#1a2e1a', '#163e16'], building: '#1f3a1f', accent: '#4ADE80' }, // Dock Planks
    9:  { sky: ['#3a0a00', '#5c1a05', '#7c2a0f'], building: '#4a1a0a', accent: '#FF4500' }, // Volcanic Rock
    10: { sky: ['#0a0a1a', '#1a1a3a', '#141428'], building: '#2a2a4a', accent: '#00FFFF' }, // Neon Highway
    11: { sky: ['#87CEEB', '#4A90D9', '#1E3A5F'], building: '#2E4A6B', accent: '#B0E0E6' }, // Frozen Tundra
    12: { sky: ['#D6E4F0', '#9DB4C0', '#5C7085'], building: '#6B7C8C', accent: '#FFFFFF' }, // Snow Path
    13: { sky: ['#0F2A0F', '#1B4D1B', '#0A1A0A'], building: '#0D2F0D', accent: '#7CFC00' }, // Jungle Trail
    14: { sky: ['#2E2A18', '#3F3A20', '#1A180C'], building: '#2A2610', accent: '#9ACD32' }, // Swamp Mud
    15: { sky: ['#1A1A1A', '#2A2A2A', '#0F0F0F'], building: '#3A3A3A', accent: '#C0C0C0' }, // Metal Grating
    16: { sky: ['#0B0221', '#1F0838', '#000000'], building: '#2A0F4A', accent: '#B0B0FF' }, // Space Station
    17: { sky: ['#3A2E1F', '#5C4A32', '#1F160E'], building: '#3A2E20', accent: '#D4A017' }, // Castle Stone
    18: { sky: ['#4A1F0A', '#8C3F1A', '#1F0F05'], building: '#3A2010', accent: '#FF6347' }, // Ruined City
    19: { sky: ['#4B0082', '#8A2BE2', '#2E0854'], building: '#4A1868', accent: '#FF00FF' }, // Crystal Cavern
    20: { sky: ['#3A0000', '#7A0000', '#1A0000'], building: '#5A0000', accent: '#FFD700' }, // Final Boss Arena
};

/**
 * Get the gradient palette for any level (clamps to 1..20 range, with
 * modular fallback for levels >20 so a hypothetical loop or endless mode
 * still renders something coherent).
 */
export function getLevelGradient(level) {
    const clamped = Math.max(1, level | 0);
    return LEVEL_GRADIENTS[clamped] || LEVEL_GRADIENTS[((clamped - 1) % 20) + 1] || LEVEL_GRADIENTS[1];
}

// Per-level enemy palettes — matched to sky+ground theme so enemies visually
// belong to each level's world. Applied by Enemy.js constructor as an overlay
// on top of the per-type default color, giving grunts/soldiers/tanks a coherent
// look with the environment while preserving type silhouettes.
//
//   primary:   main body color
//   secondary: darker body / armor plates
//   accent:    eye / detail glow color
export const LEVEL_ENEMY_PALETTES = {
    1:  { primary: '#8B5A2B', secondary: '#4A2E1A', accent: '#FF9500' }, // rustic city - brass/rust
    2:  { primary: '#5C4E42', secondary: '#2E241E', accent: '#FF3B30' }, // neon alley - grime/red
    3:  { primary: '#C2A366', secondary: '#8A6B2F', accent: '#FFD700' }, // desert - tan/gold
    4:  { primary: '#8A7A60', secondary: '#5A4A3A', accent: '#FF9500' }, // desert highway - dust
    5:  { primary: '#7A7A7A', secondary: '#3E3E3E', accent: '#5AC8FA' }, // cement - cold gray
    6:  { primary: '#8B4513', secondary: '#3A1F0A', accent: '#FF6B00' }, // factory - rust/molten
    7:  { primary: '#556B2F', secondary: '#2E3A18', accent: '#4ADE80' }, // wooden bridge - forest
    8:  { primary: '#4A6B7C', secondary: '#1F3A4A', accent: '#00CED1' }, // dock - sea teal
    9:  { primary: '#5A1A05', secondary: '#1F0505', accent: '#FF4500' }, // volcanic - lava
    10: { primary: '#00CED1', secondary: '#1F1F3A', accent: '#FF00FF' }, // neon highway - cyber
    11: { primary: '#B0E0E6', secondary: '#4A6B8C', accent: '#FFFFFF' }, // arctic - ice
    12: { primary: '#9DB4C0', secondary: '#3A4A5C', accent: '#DDDDFF' }, // snow - pale twilight
    13: { primary: '#4A6B2F', secondary: '#1A2E0A', accent: '#7CFC00' }, // jungle - deep green
    14: { primary: '#6B7A2F', secondary: '#2E3A10', accent: '#9ACD32' }, // swamp - toxic olive
    15: { primary: '#5A5A5A', secondary: '#1A1A1A', accent: '#FF0000' }, // industrial - steel
    16: { primary: '#4A2A6B', secondary: '#0F0521', accent: '#B0B0FF' }, // space station - cosmic
    17: { primary: '#6B5A32', secondary: '#2E240E', accent: '#FFD700' }, // castle - stone/gold
    18: { primary: '#8C3F1A', secondary: '#3A1005', accent: '#FF6347' }, // ruins - burnt orange
    19: { primary: '#8A2BE2', secondary: '#2E0854', accent: '#FF00FF' }, // crystal - violet
    20: { primary: '#7A0000', secondary: '#1A0000', accent: '#FFD700' }, // final boss - hell red/gold
};

/**
 * Get the enemy palette for any level (clamps + modular fallback like
 * getLevelGradient). Returns `{primary, secondary, accent}` hex strings.
 */
export function getEnemyPalette(level) {
    const clamped = Math.max(1, level | 0);
    return LEVEL_ENEMY_PALETTES[clamped] || LEVEL_ENEMY_PALETTES[((clamped - 1) % 20) + 1] || LEVEL_ENEMY_PALETTES[1];
}
